import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { Toaster as Sonner, type ToasterProps } from "sonner"

/**
 * A toast's kind is carried by its ICON colour and a light wash of the same
 * hue, never by the text (docs/ui-effects.md §6): a success toast reads green, an
 * error toast has a red glyph, a warning an amber one — and the sentence stays
 * `popover-foreground`, so it is as readable as any other.
 *
 * Built on Sonner's `richColors`, but with its own palette swapped for our
 * tokens: Sonner reads `--success-bg` / `--success-border` / `--success-text`
 * (and the error/warning/info twins) off the toaster element, and an inline
 * style there wins over its defaults. The wash is mixed into `--popover` so the
 * toast stays opaque over whatever it floats above, and follows the `.dark`
 * class because the tokens do.
 */
//
// Mixed in oklab, never oklch: `--popover` is a white with hue 0, and oklch
// walks the hue from the token's to that 0 — a green wash came out pink.
const wash = (token: string, percent: number) =>
  `color-mix(in oklab, var(${token}) ${percent}%, var(--popover))`

const kindColours = (kind: string, token: string) => ({
  [`--${kind}-bg`]: wash(token, 10),
  [`--${kind}-border`]: wash(token, 45),
  [`--${kind}-text`]: "var(--popover-foreground)",
})

// The upstream shadcn block wired theme via `next-themes`. We don't use Next:
// theme lives on the `.dark` class on <html> (see index.css), so Sonner reads
// our CSS variables directly and follows the document theme with no extra lib.
const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      className="toaster group"
      richColors
      icons={{
        success: <CircleCheckIcon className="size-4 text-success" aria-hidden />,
        info: <InfoIcon className="size-4 text-muted-foreground" aria-hidden />,
        warning: <TriangleAlertIcon className="size-4 text-warning" aria-hidden />,
        error: <OctagonXIcon className="size-4 text-destructive" aria-hidden />,
        loading: <Loader2Icon className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />,
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border)",
          "--border-radius": "var(--radius)",
          ...kindColours("success", "--success"),
          ...kindColours("error", "--destructive"),
          ...kindColours("warning", "--warning"),
          // Info is not a state: it keeps the plain toast.
          "--info-bg": "var(--popover)",
          "--info-border": "var(--border)",
          "--info-text": "var(--popover-foreground)",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
